import {
  Check,
  CheckBox,
  Close,
  ExpandMore,
  NewReleases,
  Verified,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import TranslatableTextField from "components/TranslatableTextField";
import { DEV_ANSWER_TYPE_ID } from "const";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import tasksService from "services/tasks-service";
import usersService from "services/users-service";
import { useDebounce } from "utils/useDebounce";
import Dialog from "components/NoScrollDialog";
import EmailInput from "#components/EmailsInput/index.js";
import BasicDialog from "#components/BasicDialog/index.js";
import MDButton from "#components/MDButton/index.js";

export default function TasksDialog({
  open,
  setOpen,
  accountId,
  auditId,
  auditKpis = [],
  onSubmitSuccess,
  onComplete,
}) {
  const [tasks, setTasks] = useState(
    auditKpis
      .filter(
        (auditKpi) =>
          auditKpi?.kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID &&
          !auditKpi.tasks?.length,
      )
      .map((auditKpi) => {
        const actionToTake = {};
        auditKpi.kpiAction?.expressions
          .filter((expression) => expression.code == "description")
          .forEach((expression) => {
            actionToTake[expression.languageId] = expression.content;
          });

        if (!Object.keys(actionToTake).length) {
          actionToTake.en = auditKpi.kpiAction?.description;
        }

        const title = {};
        auditKpi.kpiDeviation?.expressions
          .filter((expression) => expression.code == "description")
          .forEach((expression) => {
            title[expression.languageId] = expression.content;
          });

        if (!Object.keys(title).length) {
          title.en = auditKpi.kpiDeviation?.description;
        }

        const actionDetails = { en: "", ar: "" };
        // auditKpi.kpiAction?.expressions
        //   .filter((expression) => expression.code == "description")
        //   .forEach((expression) => {
        //     actionDetails[expression.languageId] = expression.content;
        //   });

        // if (!Object.keys(actionDetails).length) {
        //   actionDetails.en = auditKpi.kpiAction?.description;
        // }

        return {
          kpi: auditKpi,
          title,
          actionDetails,
          actionToTake,
          auditKpiId: auditKpi.id,
        };
      }),
  );
  const { label } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ priorities: null, users: null });
  const [expanded, setExpanded] = useState(null);
  const completed = tasks.map(
    (task) =>
      (task.title.en || task.title.ar) &&
      (task.actionToTake.en || task.actionToTake.ar) &&
      task.priorityId &&
      task.dueDate &&
      task.userId,
  );

  const dirty = tasks.map(
    (task) => task.priorityId || task.dueDate || task.userId,
  );

  async function getOptions() {
    const [prioritiesResponse, usersResponse] = await Promise.all([
      tasksService.getTaskPriorities(),
      usersService.getUsers({ accountId, creatingTasks: "true", view: "minimal" }),
    ]);

    if (usersResponse[0].ok) {
      setOptions((options) => ({
        ...options,
        users: usersResponse[1].map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        }),
      }));
    }

    if (prioritiesResponse[0].ok) {
      setOptions((options) => ({
        ...options,
        priorities: prioritiesResponse[1].map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        }),
      }));
    }
  }

  async function createTasks(tasks) {
    const [response, data] = await tasksService.createTasks(
      { completeAudit: auditId },
      tasks,
    );
    if (response.ok) {
      onSubmitSuccess(data);
      setOpen(false);
      return;
    }

    toast.error(data?.error || "Task creation failed.");
  }

  function changeTask(index, newTask) {
    setTasks((tasks) => {
      const newTasks = [...tasks];
      newTasks[index] = { ...newTasks[index], ...newTask };
      return newTasks;
    });
  }

  function onSplit({ task, attachments }) {
    setTasks((tasks) => {
      const newTasks = [...tasks];

      const index = newTasks.findIndex((t) => t.kpi.id == task.kpi.id);

      const toBeInserted = attachments
        .filter((a) => a.length)
        .map((split, index) => {
          return {
            ...task,
            referenceCode: task.kpi.kpi.referenceCode + "-" + (index + 1),
            inputAttachments: split,
            split: true,
            selected: true,
          };
        });

      newTasks.splice(index, 1, ...toBeInserted);
      // newTasks[index] = { ...tasks[index], splits: toBeInserted };

      // console.log("SPLIT: ", index);
      // console.log("SPLIT: OLD TASKS: ", tasks);
      // console.log("SPLIT: NEW TASKS: ", newTasks);

      return newTasks.map((t) => ({ ...t }));
    });
  }

  useEffect(() => {
    getOptions();
  }, []);

  return (
    <Dialog
      disableScrollLock={false}
      fullWidth={true}
      maxWidth={"xl"}
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          try {
            const sentTasks = tasks
              .filter((task, index) => completed[index] && task.selected)
              .map((task) => {
                return {
                  title: task.title,
                  actionDetails: task.actionDetails,
                  actionToTake: task.actionToTake,
                  priorityId: task.priorityId,
                  userId: task.userId,
                  dueDate: task.dueDate?.toDate?.() || task.dueDate?.$d || null,
                  auditKpiId: task.auditKpiId,
                  notifiedEmails: task.notifiedEmails,
                  inputAttachments: task.inputAttachments?.map((a) => a.id),
                  referenceCode: task.referenceCode,
                  imageRequired: task.imageRequired,
                };
              });

            await createTasks(sentTasks);
          } finally {
            setLoading(false);
          }
        },
      }}
    >
      <DialogTitle>{label("create-tasks-button")}</DialogTitle>
      <DialogContent>
        <Paper elevation={0} sx={{ borderRadius: "8px", overflow: "hidden" }}>
          {tasks.map((task, index) => {
            if (task.splits) {
              console.log("SPLITS: RECEIVED: ", task.splits);
              return (
                <Box
                  sx={{ outline: "2px black" }}
                  key={`Task ${task.kpi.id} ${task.referenceCode || task.kpi.kpi.referenceCode}`}
                >
                  {task.splits.map((task) => {
                    return (
                      <Task
                        expanded={expanded == index}
                        task={task}
                        key={`Task ${task.kpi.id} ${task.referenceCode || task.kpi.kpi.referenceCode}`}
                        completed={completed[index]}
                        dirty={dirty[index]}
                        onChange={(newTask) => changeTask(index, newTask)}
                        toggleExpanded={() => {
                          setExpanded((v) => {
                            if (v == index) return null;
                            return index;
                          });
                        }}
                        options={options}
                        onSplit={onSplit}
                        lastElement={index == tasks.length - 1}
                      />
                    );
                  })}
                </Box>
              );
            }
            return (
              <Task
                expanded={expanded == index}
                task={task}
                key={`Task ${task.kpi.id} ${task.referenceCode || task.kpi.kpi.referenceCode}`}
                completed={completed[index]}
                dirty={dirty[index]}
                onChange={(newTask) => changeTask(index, newTask)}
                toggleExpanded={() => {
                  setExpanded((v) => {
                    if (v == index) return null;
                    return index;
                  });
                }}
                options={options}
                onSplit={onSplit}
                lastElement={index == tasks.length - 1}
              />
            );
          })}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{label("cancel-button")}</Button>
        {/* <LoadingButton
          loading={loading}
          onClick={async () => {
            setLoading(true);
            await onComplete();
            setLoading(false);
          }}
          variant="gradient"
          color="primary"
        >
          {label("complete-without-creating-tasks")}
        </LoadingButton> */}
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
          disabled={!tasks.find((t, i) => t.selected && completed[i])}
        >
          {label("create-tasks-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function Task({
  task,
  onChange: onChangeFull,
  completed,
  dirty,
  options,
  expanded,
  toggleExpanded,
  lastElement,
  onSplit,
}) {
  const { label } = useLanguage();
  const [updated, setUpdated] = useState({});
  const [open, setOpen] = useState({});
  const debouncedUpdated = useDebounce(updated, 500);

  task = { ...task, ...updated };

  function onChange(value) {
    const newTask = { ...updated, ...value };
    if (value.dueDate || value.priorityId || value.userId) {
      newTask.selected = true;
    }
    setUpdated(newTask);
  }

  useEffect(() => {
    if (!debouncedUpdated || !Object.keys(debouncedUpdated).length) return;

    onChangeFull(debouncedUpdated);
  }, [debouncedUpdated]);

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={toggleExpanded}
      variant="outlined"
      sx={{ border: "none !important" }}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          borderBottom: lastElement && !expanded ? "none" : "1px solid",
          borderColor:
            lastElement && !expanded ? "none" : theme.palette.divider,
          backgroundColor: task.selected
            ? completed
              ? theme.palette.success.main + "44"
              : theme.palette.warning.main + "44"
            : "whitesmoke",
          position: "relative",
        })}
      >
        {open.split ? (
          <TaskSplitDialog
            open={open.split}
            setOpen={(v) => {
              setOpen({ ...open, split: v });
            }}
            data={{ task }}
            onSubmit={(attachments) => {
              onSplit({ task, attachments });
            }}
          />
        ) : null}
        <Box sx={{ height: "100%", mt: -0.75, ml: -1 }}>
          <Checkbox
            checked={Boolean(task.selected)}
            onChange={(e) => {
              e.stopPropagation();
              onChange({ selected: e.target.checked });

              toggleExpanded();
            }}
            sx={{ backgroundColor: "white" }}
          />
        </Box>
        <Typography sx={{ fontSize: "1rem", flex: 1 }}>
          {task.referenceCode || task.kpi?.kpi?.referenceCode}.{" "}
          {task.kpi?.description}{" "}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "50%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{ flex: 1 }}
            >
              <DropdownSelect
                fullWidth
                label={label("assigned-to-singular")}
                loading={!options.users}
                items={options.users || []}
                required={task.selected}
                initialValue={task.userId || ""}
                onChange={(value) => {
                  onChange({ userId: value?.id });
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                inputProps={{
                  size: "small",
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white !important",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    "&.MuiInputLabel-shrink": {
                      backgroundColor: "white !important",
                      px: 1,
                      borderRadius: "8px",
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white !important",
                  },
                }}
              />
            </Box>
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{ flex: 1 }}
            >
              <DropdownSelect
                fullWidth
                label={label("priority-singular")}
                loading={!options.priorities}
                items={options.priorities || []}
                required={task.selected}
                initialValue={task.priorityId || ""}
                onChange={(value) => {
                  onChange({ priorityId: value?.id });
                }}
                inputProps={{
                  size: "small",
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white !important",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    "&.MuiInputLabel-shrink": {
                      backgroundColor: "white !important",
                      px: 1,
                      borderRadius: "8px",
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white !important",
                  },
                }}
              />
            </Box>
            {task.inputAttachments ||
            !task.kpi.attachments.length ||
            task.kpi.attachments.length == 1 ? (
              ""
            ) : (
              <MDButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen({ ...open, split: true });
                }}
                sx={{
                  px: 1,
                  py: 0.5,
                  minHeight: 0,
                  minWidth: 0,
                  // position: "absolute",
                  // bottom: 16,
                  // left: 16,
                }}
                color="primary"
                variant="gradient"
              >
                Split
              </MDButton>
            )}
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{ display: "flex", flexDirection: "row", gap: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DateTimePicker
                  label={label("duedate-singular")}
                  value={task.dueDate}
                  sx={{ width: "100%" }}
                  onChange={(e) => {
                    if (e) onChange({ dueDate: dayjs(e.$d) });
                  }}
                  format="DD/MM/YYYY hh:mm A"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: task.selected,
                      InputProps: {
                        sx: {
                          borderRadius: "8px",
                          backgroundColor: "white !important",
                        },
                      },
                      InputLabelProps: {
                        sx: {
                          "&.MuiInputLabel-shrink": {
                            backgroundColor: "white !important",
                            px: 1,
                            borderRadius: "8px",
                          },
                        },
                      },
                      sx: {
                        borderRadius: "8px",
                        backgroundColor: "white !important",
                      },
                      fullWidth: true,
                    },
                  }}
                />
              </Box>

              <FormControlLabel
                label={"Image required"}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  margin: 0,
                  border: "1px solid transparent",
                }}
                control={
                  <Checkbox
                    checked={!!task.imageRequired}
                    onChange={(e) => {
                      onChange({ imageRequired: e.target.checked });
                    }}
                  />
                }
              />
            </Box>
          </LocalizationProvider>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            pr: 1,
            pl: 1,
          }}
        >
          {task.selected ? (
            completed ? (
              <Tooltip title="Completed">
                <Verified fontSize="small" color="success" />
              </Tooltip>
            ) : (
              <Tooltip title="Incomplete">
                <NewReleases fontSize="small" color="warning" />
              </Tooltip>
            )
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, border: "none !important" }}>
        <Grid container>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "start",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "500", ml: 1.5, mt: 1 }}
              >
                {label("kpi-singular")}
              </Typography>
            </Box>
            <Row
              label={label("deviation-singular")}
              content={task.kpi?.kpiDeviation?.description}
            />{" "}
            <Divider
              sx={{
                margin: "0 auto",
                width: "calc(100%)",
                backgroundColor: "rgba(52, 71, 103, 0.4)",
                backgroundImage: "unset",
              }}
            />
            <Row
              label={label("risk-singular")}
              content={task.kpi?.kpiRisk?.description}
            />
            <Divider
              sx={{
                margin: "0 auto",
                width: "calc(100%)",
                backgroundColor: "rgba(52, 71, 103, 0.4)",
                backgroundImage: "unset",
              }}
            />{" "}
            <Row
              label={label("action-singular")}
              content={task.kpi?.kpiAction?.description}
            />
            <Divider
              sx={{
                margin: "0 auto",
                width: "calc(100%)",
                backgroundColor: "rgba(52, 71, 103, 0.4)",
                backgroundImage: "unset",
              }}
            />
            <Row
              label={label("attachment-plural")}
              content={
                (task.inputAttachments || task.kpi.attachments)?.length ? (
                  <ThumbnailAttachments
                    attachments={task.inputAttachments || task.kpi.attachments}
                  />
                ) : (
                  label("none")
                )
              }
            />
            <Divider
              sx={{
                margin: "0 auto",
                width: "calc(100%)",
                backgroundColor: "rgba(52, 71, 103, 0.4)",
                backgroundImage: "unset",
              }}
            />
          </Grid>
        </Grid>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: "500", mb: 1 }}>
            {label("task-singular")}
          </Typography>
          <Grid container spacing={1}>
            <Grid container item spacing={1} xs={6}>
              <Grid item xs={12}>
                <TranslatableTextField
                  fullWidth
                  label={label("title-singular")}
                  required={task.selected}
                  values={task.title}
                  onChange={(e) => {
                    onChange({ title: e.target.value });
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TranslatableTextField
                  fullWidth
                  label={label("additional-details")}
                  multiline
                  minRows={1}
                  values={task.actionDetails}
                  onChange={(e) => {
                    onChange({ actionDetails: e.target.value });
                  }}
                />
              </Grid>
            </Grid>
            <Grid container item xs={6} spacing={1}>
              <Grid item xs={12}>
                <TranslatableTextField
                  fullWidth
                  label={label("description-singular")}
                  multiline
                  required={task.selected}
                  values={task.actionToTake}
                  onChange={(e) => {
                    onChange({ actionToTake: e.target.value });
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <EmailInput
                label="Notified emails (press Enter to add)"
                value={task.notifiedEmails || ""}
                onChange={(newValue) => {
                  let v = task;

                  let notifiedEmails;
                  if (typeof newValue == "function")
                    notifiedEmails = newValue(
                      v.notifiedEmails
                        ?.split(",")
                        .map((v) => v.trim())
                        .filter(Boolean) || [],
                    ).join(",");
                  else notifiedEmails = newValue.join(",");

                  onChange({ notifiedEmails });
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function Row({ label, content }) {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "start",
        px: 1.5,
        py: 0.5,
        gap: 2,
      }}
    >
      <Typography
        variant="body2"
        color="GrayText"
        sx={{ fontWeight: "400", lineHeight: "20px" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: "400", textAlign: "end", lineHeight: "20px" }}
      >
        {content}
      </Typography>
    </Box>
  );
}

function TaskSplitDialog({ open, setOpen, data, onSubmit }) {
  const { label } = useLanguage();
  const [attachments, setAttachments] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (data.task.kpi.attachments?.length) {
      setAttachments(
        Array.from({ length: data.task.kpi.attachments?.length }).map(() => []),
      );
    }
  }, [data.task.kpi.attachments?.length]);

  function errors() {
    if (
      attachments.find((v) => v.length == data.task.kpi.attachments?.length) ||
      !attachments.flat().length
    ) {
      return "Split the attachments, as this setup won't change anything.";
    }
  }

  return (
    <BasicDialog
      maxWidth={"md"}
      fullWidth={true}
      title={"Split task"}
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: "400" }}
            variant="caption"
          >
            Drag the attachments to different tasks to assign them as you see
            fit. Each attachment or group of attachments can be assigned to a
            task.
          </Typography>
          <Card variant="outlined" sx={{ p: 2, pt: 1 }}>
            <Typography variant="h6">No task</Typography>
            <Box>
              <ThumbnailAttachments
                attachments={data.task.kpi.attachments.filter(
                  (v) => !attachments.flat().find((t) => t.id == v.id),
                )}
                containerProps={{
                  onDrop: () => {
                    setAttachments((attachments) => {
                      return [...attachments].map((v) =>
                        v.filter((t) => t.id != current.id),
                      );
                    });
                    setCurrent(null);
                  },
                  onDragOver: (e) => {
                    e.preventDefault();
                  },
                }}
                cardProps={(attachment) => ({
                  draggable: true,
                  onDrag: () => {
                    setCurrent(attachment);
                  },
                  sx: {
                    cursor: "grab !important",
                  },
                })}
                total={data.task.kpi.attachments.length}
              />
            </Box>
          </Card>
          <Grid container spacing={1}>
            {data.task.kpi.attachments.map((attachment, index) => {
              return (
                <Grid item xs={6}>
                  <Card variant="outlined" sx={{ p: 2, pt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="h6">Task #{index + 1}</Typography>
                      <Chip
                        icon={
                          attachments?.[index]?.length > 0 ? (
                            <Check />
                          ) : (
                            <Close />
                          )
                        }
                        size="small"
                        variant="filled"
                        color={
                          attachments?.[index]?.length > 0
                            ? "primary"
                            : "default"
                        }
                        label={
                          attachments?.[index]?.length > 0
                            ? "Will be created"
                            : "Will not be Created"
                        }
                      />
                    </Box>
                    <Box>
                      <ThumbnailAttachments
                        attachments={attachments?.[index]}
                        total={data.task.kpi.attachments.length}
                        containerProps={{
                          onDrop: () => {
                            console.log("CARD PROPS: ON DROP!");
                            setAttachments((prev) =>
                              prev.map((arr, i) =>
                                i === index
                                  ? [
                                      ...arr.filter((v) => v.id != current.id),
                                      current,
                                    ]
                                  : arr.filter((v) => v.id != current.id),
                              ),
                            );
                            setCurrent(null);
                          },
                          onDragOver: (e) => {
                            e.preventDefault();
                          },
                        }}
                        cardProps={(attachment) => ({
                          draggable: true,
                          onDrag: () => {
                            setCurrent(attachment);
                          },
                          sx: {
                            cursor: "grab !important",
                          },
                        })}
                      />
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      }
      actions={[
        {
          text: "Split",
          onClick: () => {
            onSubmit(attachments);
            setOpen(false);
          },
          props: {
            variant: "gradient",
            color: "info",
            disabled: !!errors(),
          },
        },
      ]}
      cancelButton={true}
      open={open}
      setOpen={setOpen}
    />
  );
}

function ThumbnailAttachments({
  attachments,
  total,
  cardProps = () => ({}),
  containerProps = {},
}) {
  const { label } = useLanguage();
  const [open, setOpen] = useState({});

  return (
    <>
      {Boolean(open.attachment) ? (
        <BasicDialog
          maxWidth="lg"
          fullWidth
          title={label("attachment-singular")}
          content={
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
                height: "calc(100vh - 224px)",
              }}
            >
              {open.attachment?.type?.startsWith("audio") ? (
                <Box sx={{ width: "20%", textAlign: "center" }}>
                  <Icon sx={{ fontSize: "100px !important", mb: 2 }}>
                    audiotrack
                  </Icon>
                  <audio
                    controls
                    src={open.attachment.url}
                    style={{ width: "100%" }}
                  />
                </Box>
              ) : (
                <img
                  style={{
                    height: "100%",
                    borderRadius: "14px",
                  }}
                  src={open?.attachment?.url}
                />
              )}
              <Card
                sx={{ flex: 1, height: "100%", width: "100%", p: 2 }}
                variant="outlined"
              >
                <Row
                  label={label("remark-plural")}
                  content={
                    open?.attachment?.remarks || (
                      <Typography
                        color="secondary"
                        sx={{ fontSize: "16px", fontStyle: "italic" }}
                      >
                        None
                      </Typography>
                    )
                  }
                />
              </Card>
            </Box>
          }
          cancelButton={true}
          open={Boolean(open.attachment)}
          setOpen={(v) => {
            setOpen({ ...open, attachment: !v ? false : {} });
          }}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          flexWrap: "wrap",
        }}
        {...containerProps}
      >
        {attachments?.map((attachment) => {
          const isAudio = attachment.type?.startsWith("audio");

          return (
            <Card
              variant="outlined"
              {...cardProps(attachment)}
              draggable
              key={"Attachment: " + attachment.id}
            >
              <CardActionArea
                onClick={() => {
                  setOpen({ ...open, attachment });
                }}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "start",
                  gap: 1,
                  cursor: cardProps?.(attachment)?.sx?.cursor || "pointer",
                }}
              >
                <Box
                  sx={{
                    height: "80px",
                    width: "80px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: isAudio ? "whitesmoke" : "transparent",
                    borderRadius: "9px",
                    overflow: "hidden",
                  }}
                >
                  {isAudio ? (
                    <Icon color="info">audiotrack</Icon>
                  ) : (
                    <img
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                      src={attachment.url}
                    />
                  )}
                </Box>
                {attachment.remarks ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "400",
                      lineHeight: "18px",
                      fontSize: "15px",
                      py: 1,
                      pr: 1,
                    }}
                    color="GrayText"
                  >
                    {attachment.remarks}
                  </Typography>
                ) : null}
              </CardActionArea>
            </Card>
          );
        })}

        {total && attachments?.length < total
          ? Array.from({ length: total - attachments?.length }).map((_, i) => (
              <Box
                key={`empty-${i}`}
                sx={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "whitesmoke",
                  borderRadius: "9px",
                }}
              />
            ))
          : null}
      </Box>
    </>
  );
}
